import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { StyleQuizHeelFrequency, StyleQuizStep } from '../style-quiz.data'

/**
 * Style quiz — open / close and what survives a reload. Answers themselves live on the
 * server (saved at every step, R203); only what the API cannot hold yet is local
 * (BE-151): the step to resume at, Rarely, the number of outfits rated.
 */
const STORAGE_KEY = 'fair.style-quiz'

export type StyleQuizEntry =
  /** From the top, or the step the user left at. */
  | { mode: 'resume' }
  /** Rate intro with these styles ticked (Style › Rate all). */
  | { mode: 'rate'; styleIds?: number[] }

interface StyleQuizSaved {
  step?: StyleQuizStep
  rarely?: boolean
  rated?: number
}

function readSaved(): StyleQuizSaved {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as StyleQuizSaved
  } catch {
    return {}
  }
}

function writeSaved(patch: StyleQuizSaved) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readSaved(), ...patch }))
  } catch {
    // Private mode / quota — the quiz still works, it just starts over next time.
  }
}

export const useStyleQuizStore = defineStore('styleQuiz', () => {
  const isOpen = ref(false)
  const entry = ref<StyleQuizEntry>({ mode: 'resume' })
  /** Bumped on every open so the quiz re-reads the profile. */
  const session = ref(0)

  function open(next: StyleQuizEntry = { mode: 'resume' }) {
    entry.value = next
    session.value += 1
    isOpen.value = true
  }

  /** Style › Rate all / a style's "Rate": straight to Rate intro. */
  function openRate(styleIds?: number[]) {
    open({ mode: 'rate', styleIds })
  }

  function close() {
    isOpen.value = false
  }

  function savedStep(): StyleQuizStep | null {
    return readSaved().step ?? null
  }

  function saveStep(step: StyleQuizStep) {
    // Welcome and Result are not places to come back to.
    writeSaved({ step: step === 'result' ? undefined : step })
  }

  function heelsRarely(): boolean {
    return Boolean(readSaved().rarely)
  }

  function saveHeelsFrequency(value: StyleQuizHeelFrequency) {
    writeSaved({ rarely: value === 'rarely' })
  }

  function ratedCount(): number {
    return readSaved().rated ?? 0
  }

  function addRated() {
    writeSaved({ rated: ratedCount() + 1 })
  }

  return {
    isOpen,
    entry,
    session,
    open,
    openRate,
    close,
    savedStep,
    saveStep,
    heelsRarely,
    saveHeelsFrequency,
    ratedCount,
    addRated,
  }
})
